import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContextWeb } from "../../context/AuthContextWeb";
import { apiRequest } from "../../lib/apiClient";

export default function OwnerListingsPage() {
  const { user } = useContext(AuthContextWeb);
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchProperties = useCallback(async () => {
    if (!user?.userId) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest(`/properties/owner/${user.userId}`);
      setProperties(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Failed to load properties.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    setConfirmDeleteId(null);
    try {
      await apiRequest(`/properties/${id}`, { method: "DELETE" });
      setProperties((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err?.message || "Failed to delete property.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="owner-page">
      <div className="owner-page-header">
        <div>
          <h1 className="owner-page-title">My Listings</h1>
          <p className="owner-page-subtitle">
            {loading
              ? ""
              : `${properties.length} propert${properties.length === 1 ? "y" : "ies"}`}
          </p>
        </div>
        <button
          type="button"
          className="owner-action-btn owner-action-btn--primary"
          onClick={() => navigate("/owner/add-property")}
        >
          + Add Property
        </button>
      </div>

      {error && <p className="form-error">{error}</p>}

      {loading ? (
        <p className="loading-message">Loading…</p>
      ) : properties.length === 0 ? (
        <div className="owner-empty-state">
          <p className="owner-empty-text">
            No listings yet. Add your first property!
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/owner/add-property")}
          >
            Add Property
          </button>
        </div>
      ) : (
        <div className="owner-listings-table-wrap">
          <table className="owner-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Location</th>
                <th>Rent/mo</th>
                <th>Beds</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p) => {
                const cover = p.images?.[0]?.url;
                return (
                  <tr key={p.id}>
                    <td>
                      <div className="owner-table-prop-cell">
                        {cover ? (
                          <img
                            src={cover}
                            alt={p.title}
                            className="owner-table-thumb"
                          />
                        ) : (
                          <div className="owner-table-thumb owner-table-thumb--empty">
                            📷
                          </div>
                        )}
                        <span className="owner-table-prop-title">
                          {p.title || "Untitled"}
                        </span>
                      </div>
                    </td>
                    <td>{p.location || p.city || "—"}</td>
                    <td>
                      {p.price != null
                        ? new Intl.NumberFormat("en-NP", {
                            style: "currency",
                            currency: "NPR",
                            maximumFractionDigits: 0,
                          }).format(p.price)
                        : "—"}
                    </td>
                    <td>{p.bedrooms ?? "—"}</td>
                    <td>
                      <span className="owner-type-badge">
                        {(p.propertyType || "—").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td>
                      <div className="owner-table-actions">
                        <button
                          type="button"
                          className="owner-tbl-btn"
                          onClick={() => navigate(`/owner/properties/${p.id}`)}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="owner-tbl-btn owner-tbl-btn--edit"
                          onClick={() =>
                            navigate(`/owner/edit-property/${p.id}`)
                          }
                        >
                          Edit
                        </button>
                        {confirmDeleteId === p.id ? (
                          <>
                            <button
                              type="button"
                              className="owner-tbl-btn owner-tbl-btn--danger"
                              disabled={deletingId === p.id}
                              onClick={() => handleDelete(p.id)}
                            >
                              {deletingId === p.id ? "Deleting…" : "Confirm"}
                            </button>
                            <button
                              type="button"
                              className="owner-tbl-btn"
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="owner-tbl-btn owner-tbl-btn--danger"
                            onClick={() => setConfirmDeleteId(p.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
